"use client";
import { useState, useCallback, Dispatch, SetStateAction, useRef } from "react";
import { ReadonlyURLSearchParams } from "next/navigation";
import * as Sentry from "@sentry/nextjs";
import { Maybe } from "graphql/jsutils/Maybe";
import { ApiError } from "@/common/api/api-client";
import { Club, } from "@/generated/contentfulTypes";
import { Languages } from "@/common/utils/constants";
import { handleWithPayment } from "../components/ExtensionFlowContent/StepsContent/components/ExtensionTermsAndCondition/ExtensionTermsAndCondition.service";
import { getSignUpAmount } from "@/common/utils/paymentUtils";

interface UsePaymentSessionParams {
    translations: Record<string, string>;
    searchParams: ReadonlyURLSearchParams;
    club: Maybe<Club>;
    lang: Languages;
    updateUrl: (params: Record<string, string>) => void;
    setIsPaymentWidgetVisible: Dispatch<SetStateAction<boolean>>;
    paymentSessionKey?: string | null;
    amount?: number;
}

export const usePaymentSession = ({
    translations,
    searchParams,
    club,
    lang,
    updateUrl,
    setIsPaymentWidgetVisible,
    paymentSessionKey,
    amount
}: UsePaymentSessionParams) => {
    const [isPaymentSessionLoading, setIsPaymentSessionLoading] = useState(false);
    const [isPaymentSessionFailed, setIsPaymentSessionFailed] = useState(false);
    const activeSessionKeyRef = useRef<string | null>(null);
    const processedSessionKeyRef = useRef<string | null>(null);
    const isSessionRunningRef = useRef(false);

    const signUpAmount = getSignUpAmount(club, amount)

    const triggerPaymentSession = useCallback(async () => {
        try {
            if (
                !paymentSessionKey ||
                signUpAmount == null ||
                activeSessionKeyRef.current === paymentSessionKey ||
                processedSessionKeyRef.current === paymentSessionKey ||
                isSessionRunningRef.current
            ) {
                return;
            }

            processedSessionKeyRef.current = paymentSessionKey;
            isSessionRunningRef.current = true;
            setIsPaymentSessionLoading(true);

            const referenceText = translations["paymentmethod.widget.referencetext"];
            await handleWithPayment({
                searchParams,
                club,
                referenceText,
                lang,
                amount: signUpAmount
            });
            activeSessionKeyRef.current = paymentSessionKey;
            setIsPaymentWidgetVisible(true)
        } catch (error) {
            setIsPaymentSessionFailed(true);
            Sentry.captureException(error);
            console.error("Start of payment session failed: ", error);

            if (error instanceof ApiError) {
                const status = error.status;
                const code =
                    (error.data && (error.data.code as string)) ||
                    (error.data && (error.data.error as string));

                if ((status === 400 || status === 403) && code) {
                    updateUrl({ error: code });
                }
            }
        } finally {
            setIsPaymentSessionLoading(false);
            isSessionRunningRef.current = false;
        }
    }, [translations, searchParams, club, lang, updateUrl, signUpAmount, paymentSessionKey, setIsPaymentWidgetVisible]);

    return { isPaymentSessionLoading, isPaymentSessionFailed, triggerPaymentSession };
};
